import { callQuery, GET_FILE_AT_VERSION, GET_ALL_VERSIONS_FOR_CSN_AND_FILE_TYPE } from "../../../amplify/appsync/query";
import { PatientVisit } from "../../../amplify/appsync/search-patients";
import { readBinaryFile } from "../../../amplify/s3/storage";

// Module types that should fall back to previous versions on error
const FALLBACK_ENABLED_TYPES = [
    'explain_ddx',
    'explain_anatomy',
    'explain_physiology',
    'explain_charting',
    'explain_patient_summary',
    'explain_current_visit',
    'explain_risk',
    'explain_monitor',
    'explain_palliative',
    'explain_sx_rx',
    'explain_scores',
];

// Maximum number of previous versions to try before giving up
const MAX_FALLBACK_ATTEMPTS = 3;

class DashboardLLMCache {
    private static instance: DashboardLLMCache;
    private cache: Map<string, any>;
    private readonly CACHE_DURATION = 0; // Turn off cache

    private constructor() {
        this.cache = new Map();
    }

    public static getInstance(): DashboardLLMCache {
        if (!DashboardLLMCache.instance) {
            DashboardLLMCache.instance = new DashboardLLMCache();
        }
        return DashboardLLMCache.instance;
    }

    private generateCacheKey(type: string, patient: PatientVisit, versionId?: string) {
        let key = `${type}-${patient.csn}`;
        if (versionId) key += `-v${versionId}`;
        return key;
    }

    /**
     * Check if data contains an error (empty/failed LLM response)
     */
    private hasError(data: any): boolean {
        if (!data) return true;
        // Check for explicit error field
        if (data.error) return true;
        // Check for empty prioritized_diagnoses (DDX-specific)
        if (data.prioritized_diagnoses !== undefined &&
            Array.isArray(data.prioritized_diagnoses) &&
            data.prioritized_diagnoses.length === 0 &&
            (!data.ddx_summary || data.ddx_summary === '')) {
            return true;
        }
        return false;
    }

    /**
     * Fetch a specific version of a file
     */
    private async fetchVersion(
        patient: PatientVisit,
        type: string,
        versionId: string
    ): Promise<any> {
        const response: any = await callQuery({
            operation: GET_FILE_AT_VERSION,
            mrn: patient.mrn,
            csn: patient.csn,
            file_type: type,
            version_id: versionId,
        });
        return response;
    }

    /**
     * Try to fetch a valid version, falling back to older versions if the latest has an error
     */
    private async fetchWithFallback(
        patient: PatientVisit,
        tenant: string,
        type: string
    ): Promise<any> {
        // First, try the latest version
        const remoteData = await readBinaryFile(
            `llm_dashboard/mrn=${patient.mrn}/csn=${patient.csn}/${type}.json`,
            tenant,
            `ed-llm-dashboard`
        );
        const decoder = new TextDecoder();
        const latestData = JSON.parse(decoder.decode(remoteData));

        // If latest is valid or fallback not enabled for this type, return it
        if (!this.hasError(latestData) || !FALLBACK_ENABLED_TYPES.includes(type)) {
            return latestData;
        }

        console.warn(`[DashboardLLMCache] Latest ${type} has error, attempting fallback to previous version`);

        // Get all versions for this file type
        try {
            const allVersions: any[] = await callQuery({
                operation: GET_ALL_VERSIONS_FOR_CSN_AND_FILE_TYPE,
                csn: patient.csn,
                file_type: type,
            });

            if (!allVersions || allVersions.length <= 1) {
                console.warn(`[DashboardLLMCache] No previous versions available for ${type}`);
                return latestData; // Return errored data if no fallback available
            }

            // Try older versions (skip index 0 which is the latest we already tried)
            for (let i = 1; i < Math.min(allVersions.length, MAX_FALLBACK_ATTEMPTS + 1); i++) {
                const version = allVersions[i];
                try {
                    const olderData = await this.fetchVersion(patient, type, version.version_id);
                    if (!this.hasError(olderData)) {
                        console.log(`[DashboardLLMCache] Successfully fell back to ${type} version from ${version.version_date}`);
                        // Mark that this is a fallback version so UI can show indicator if needed
                        olderData._fallback_version = true;
                        olderData._fallback_version_date = version.version_date;
                        return olderData;
                    }
                } catch (e) {
                    console.warn(`[DashboardLLMCache] Failed to fetch fallback version ${version.version_id}:`, e);
                }
            }

            console.warn(`[DashboardLLMCache] All fallback attempts failed for ${type}, returning errored data`);
            return latestData;
        } catch (e) {
            console.error(`[DashboardLLMCache] Failed to get version list for fallback:`, e);
            return latestData;
        }
    }

    public async get(
        type: string,
        patient: PatientVisit,
        tenant: string,
        versionId?: string
    ) {
        if (!patient || !type) return undefined;

        const cacheKey = this.generateCacheKey(type, patient, versionId);

        // If caching is disabled (CACHE_DURATION = 0), skip cache entirely
        if (this.CACHE_DURATION === 0) {
            try {
                let data;
                if (versionId) {
                    // Fetch specific version via GET_FILE_AT_VERSION (no fallback for explicit version requests)
                    const response: any = await callQuery({
                        operation: GET_FILE_AT_VERSION,
                        mrn: patient.mrn,
                        csn: patient.csn,
                        file_type: type,
                        version_id: versionId,
                    });
                    data = response;
                } else {
                    // Fetch latest version with fallback support
                    data = await this.fetchWithFallback(patient, tenant, type);
                }
                return data;
            } catch (e) {
                return undefined;
            }
        }

        // Check local cache first (only when caching is enabled)
        const cachedData = this.cache.get(cacheKey);
        if (cachedData) {
            console.log(`FOUND local cache for key: ${cacheKey}`);
            if (Date.now() - cachedData.timestamp > this.CACHE_DURATION) {
                this.cache.delete(cacheKey);
            } else {
                return cachedData;
            }
        }

        try {
            let data;
            if (versionId) {
                // Fetch specific version via GET_FILE_AT_VERSION (no fallback for explicit version requests)
                const response: any = await callQuery({
                    operation: GET_FILE_AT_VERSION,
                    mrn: patient.mrn,
                    csn: patient.csn,
                    file_type: type,
                    version_id: versionId,
                });
                data = response;
            } else {
                // Fetch latest version with fallback support
                data = await this.fetchWithFallback(patient, tenant, type);
            }

            // Cache the data locally (only when caching is enabled)
            if (this.CACHE_DURATION > 0) {
                this.cache.set(cacheKey, { ...data, timestamp: Date.now() });
            }

            return data;
        } catch (e) {
            return undefined;
        }
    }

    public clear(): void {
        this.cache.clear();
    }
}

export const dashboardLLMCache = DashboardLLMCache.getInstance();
